// ============================================================================
// OPPORTUNITY DIALOG — link a Dynamics opportunity to a booking, or edit one
// Values entered here are the link-time snapshot; once a Dynamics sync has
// written values (syncStatus ok) they lock and only the attribution can change.
// ============================================================================

import React, { useState } from 'react';
import { AlertCircle, Link2, Lock, Loader, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  BookingOpportunityLink,
  CrmAttribution,
  CrmCurrency,
  OpportunityState,
  OpportunityValues,
  CRM_CURRENCIES,
  OPPORTUNITY_STATES,
  STATE_LABELS,
  ATTRIBUTION_LABELS,
  parseOpportunityRef,
  linkOpportunity,
  updateOpportunity
} from '../../services/studio-crm';

interface Props {
  bookingId: string;
  bookingLabel: string;
  existing?: BookingOpportunityLink;
  onClose: () => void;
  onSaved: (link: BookingOpportunityLink) => void;
}

const inputCls = 'w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none disabled:bg-slate-50 disabled:text-slate-400';
const labelCls = 'block text-sm font-semibold text-navy mb-2';

function Segmented<T extends string>({ options, value, labels, onChange, disabled }: {
  options: T[];
  value: T | '';
  labels: Record<T, string>;
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <div className={`flex rounded-xl border border-[#e2e8f0] overflow-hidden ${disabled ? 'opacity-50' : ''}`}>
      {options.map(opt => (
        <button key={opt} type="button" disabled={disabled} onClick={() => onChange(opt)}
          className={`flex-1 py-2 text-xs font-semibold transition-all ${
            value === opt ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
          }`}>
          {labels[opt]}
        </button>
      ))}
    </div>
  );
}

export default function OpportunityDialog({ bookingId, bookingLabel, existing, onClose, onSaved }: Props) {
  const initial = existing?.snapshot;
  const valuesLocked = existing?.syncStatus === 'ok';

  const [ref, setRef] = useState(existing?.opportunityId || '');
  const [name, setName] = useState(initial?.name || '');
  const [state, setState] = useState<OpportunityState>(initial?.state || 'open');
  const [stage, setStage] = useState(initial?.stage || '');
  const [value, setValue] = useState(initial ? String(initial.value) : '');
  const [currency, setCurrency] = useState<CrmCurrency | ''>(initial?.currency || '');
  const [closeDate, setCloseDate] = useState(initial?.closeDate || '');
  const [attribution, setAttribution] = useState<CrmAttribution | ''>(existing?.attribution || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsedId = existing ? existing.opportunityId : parseOpportunityRef(ref);

  function hasValueChanges(): boolean {
    if (!initial || valuesLocked) return false;
    return name.trim() !== initial.name || state !== initial.state || Number(value) !== initial.value
      || currency !== initial.currency || stage.trim() !== (initial.stage || '') || closeDate !== (initial.closeDate || '');
  }

  function validate(): string | null {
    if (!parsedId) return 'Paste the opportunity URL from Dynamics, or its GUID.';
    if (!attribution) return 'Choose whether the session sourced or influenced this opportunity.';
    if (valuesLocked) return null;
    if (!name.trim()) return 'Opportunity name is required.';
    const num = Number(value);
    if (value.trim() === '' || !Number.isFinite(num) || num < 0) return 'Value must be a number of 0 or more.';
    if (!currency) return 'Choose a currency.';
    return null;
  }

  async function handleSave() {
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    if (existing && attribution === existing.attribution && !hasValueChanges()) {
      onClose();
      return;
    }
    const snapshot: OpportunityValues = {
      name: name.trim(),
      state,
      value: Number(value),
      currency: currency as CrmCurrency,
      ...(stage.trim() ? { stage: stage.trim() } : {}),
      ...(closeDate ? { closeDate } : {})
    };

    setSaving(true);
    setError(null);
    try {
      if (existing) {
        // Only send values that changed, so an attribution-only edit still
        // succeeds if a sync has locked the snapshot since the page loaded.
        const saved = await updateOpportunity(bookingId, existing.opportunityId, {
          ...(attribution !== existing.attribution ? { attribution: attribution as CrmAttribution } : {}),
          ...(hasValueChanges() ? { snapshot } : {})
        });
        toast.success('Opportunity updated');
        onSaved(saved);
      } else {
        const saved = await linkOpportunity(bookingId, { opportunityRef: ref, attribution: attribution as CrmAttribution, snapshot });
        toast.success('Opportunity linked');
        onSaved(saved);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save opportunity');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden shadow-xl font-display"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2e8f0]">
          <div>
            <h2 className="text-lg font-bold text-navy">{existing ? 'Edit opportunity' : 'Link opportunity'}</h2>
            <p className="text-xs text-slate-500">{bookingLabel}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X size={16} /></button>
        </div>

        {error && (
          <div className="px-5 py-4 border-b border-[#e2e8f0] bg-red-50 flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm font-medium text-red-700">{error}</p>
          </div>
        )}

        <div className="p-5 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className={labelCls}>Dynamics opportunity</label>
            <input className={inputCls} value={ref} disabled={!!existing} onChange={e => setRef(e.target.value)}
              placeholder="Paste the opportunity URL from Dynamics" />
            <p className={`mt-2 text-xs ${parsedId ? 'text-slate-400' : ref ? 'text-red-500' : 'text-slate-400'}`}>
              {parsedId ? <>ID <span className="font-mono">{parsedId}</span></> : ref ? 'No opportunity ID found in that text' : 'Open the opportunity in Dynamics and copy the address bar.'}
            </p>
          </div>

          <div>
            <label className={labelCls}>Session impact</label>
            <Segmented options={['sourced', 'influenced'] as CrmAttribution[]} value={attribution}
              labels={ATTRIBUTION_LABELS} onChange={setAttribution} />
            <p className="mt-2 text-xs text-slate-400">Sourced: the opportunity came out of the session. Influenced: it already existed and the session moved it on.</p>
          </div>

          {valuesLocked && (
            <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-[#e2e8f0] rounded-xl px-3 py-2">
              <Lock size={12} /> Values have synced from Dynamics, so the link-time values are locked.
            </div>
          )}

          <div>
            <label className={labelCls}>Name</label>
            <input className={inputCls} value={name} disabled={valuesLocked} onChange={e => setName(e.target.value)} maxLength={200} />
          </div>

          <div>
            <label className={labelCls}>State</label>
            <Segmented options={OPPORTUNITY_STATES} value={state} labels={STATE_LABELS} onChange={setState} disabled={valuesLocked} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{state === 'won' ? 'Actual value (TCV)' : 'Estimated value (TCV)'}</label>
              <input className={inputCls} type="number" min={0} step="any" value={value} disabled={valuesLocked}
                onChange={e => setValue(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Currency</label>
              <select className={`${inputCls} bg-white`} value={currency} disabled={valuesLocked}
                onChange={e => setCurrency(e.target.value as CrmCurrency)}>
                <option value="">Choose…</option>
                {CRM_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Stage <span className="font-normal text-slate-400">(optional)</span></label>
              <input className={inputCls} value={stage} disabled={valuesLocked} onChange={e => setStage(e.target.value)} maxLength={200} />
            </div>
            <div>
              <label className={labelCls}>Close date <span className="font-normal text-slate-400">(optional)</span></label>
              <input className={inputCls} type="date" value={closeDate} disabled={valuesLocked} onChange={e => setCloseDate(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#e2e8f0] bg-slate-50/50">
          <button onClick={onClose}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60">
            {saving ? <Loader size={16} className="animate-spin" /> : <Link2 size={16} />}
            {existing ? 'Save' : 'Link'}
          </button>
        </div>
      </div>
    </div>
  );
}
